/**
 * Guided Tour Component for RepConnect
 * Provides interactive onboarding and feature discovery
 * 
 * Copyright (c) 2025 RepConnect, LLC
 * 
 */

import React, { useState, useEffect, useRef } from 'react';
import { ArrowRight, ArrowLeft, X, Lightbulb, ChevronRight } from 'lucide-react';
import ContextualHelp from '../utils/contextualHelp';
import { logger } from '../utils/logger';

const GuidedTour = ({
  isActive = false,
  userType = 'customer',
  onComplete,
  onSkip,
  onStepChange,
  autoStart = false
}) => {
  const [currentStep, setCurrentStep] = useState(0);
  const [isVisible, setIsVisible] = useState(false);
  const [highlightElement, setHighlightElement] = useState(null);
  const [tourSteps, setTourSteps] = useState([]);
  const overlayRef = useRef(null);
  const tooltipRef = useRef(null);

  useEffect(() => {
    if (isActive) {
      const steps = ContextualHelp.getGuidedTour(userType);
      setTourSteps(steps);
      if (autoStart) {
        setTimeout(startTour, 500);
      }
    }
  }, [isActive, userType, autoStart]);

  const startTour = () => {
    if (tourSteps.length === 0) return;
    setIsVisible(true);
    setCurrentStep(0);
    highlightStep(0);
  };

  const highlightStep = (stepIndex) => {
    if (!tourSteps[stepIndex]) return;

    const target = document.querySelector(tourSteps[stepIndex].target);
    if (target) {
      setHighlightElement(target);
      
      // Scroll target into view
      target.scrollIntoView({
        behavior: 'smooth',
        block: 'center',
        inline: 'center'
      });

      // Position tooltip
      setTimeout(() => positionTooltip(target, stepIndex), 300);
    } else {
      logger.warn(`Tour target not found: ${tourSteps[stepIndex].target}`);
    }
  };

  const positionTooltip = (target, stepIndex) => {
    if (!tooltipRef.current || !target) return;

    const targetRect = target.getBoundingClientRect();
    const tooltipRect = tooltipRef.current.getBoundingClientRect();
    const viewport = {
      width: window.innerWidth,
      height: window.innerHeight
    };

    let position = { top: 0, left: 0 };
    const step = tourSteps[stepIndex];
    const placement = step.placement || 'bottom';

    // Calculate position based on placement
    switch (placement) {
      case 'top':
        position.top = targetRect.top - tooltipRect.height - 16;
        position.left = targetRect.left + (targetRect.width - tooltipRect.width) / 2;
        break;
      case 'bottom':
        position.top = targetRect.bottom + 16;
        position.left = targetRect.left + (targetRect.width - tooltipRect.width) / 2;
        break;
      case 'left':
        position.top = targetRect.top + (targetRect.height - tooltipRect.height) / 2;
        position.left = targetRect.left - tooltipRect.width - 16;
        break;
      case 'right':
        position.top = targetRect.top + (targetRect.height - tooltipRect.height) / 2;
        position.left = targetRect.right + 16;
        break;
      case 'center':
        position.top = (viewport.height - tooltipRect.height) / 2;
        position.left = (viewport.width - tooltipRect.width) / 2;
        break;
      default:
        position.top = targetRect.bottom + 16;
        position.left = targetRect.left + (targetRect.width - tooltipRect.width) / 2;
    }

    // Ensure tooltip stays within viewport
    position.left = Math.max(16, Math.min(position.left, viewport.width - tooltipRect.width - 16));
    position.top = Math.max(16, Math.min(position.top, viewport.height - tooltipRect.height - 16));

    tooltipRef.current.style.top = `${position.top}px`;
    tooltipRef.current.style.left = `${position.left}px`;
  };

  const nextStep = () => {
    if (currentStep < tourSteps.length - 1) {
      const newStep = currentStep + 1;
      setCurrentStep(newStep);
      highlightStep(newStep);
      if (onStepChange) onStepChange(newStep, tourSteps[newStep]);
    } else {
      completeTour();
    }
  };

  const prevStep = () => {
    if (currentStep > 0) {
      const newStep = currentStep - 1;
      setCurrentStep(newStep);
      highlightStep(newStep);
      if (onStepChange) onStepChange(newStep, tourSteps[newStep]);
    }
  };

  const completeTour = () => {
    setIsVisible(false);
    setHighlightElement(null);
    if (onComplete) onComplete();
  };

  const skipTour = () => {
    setIsVisible(false);
    setHighlightElement(null);
    if (onSkip) onSkip();
  };

  // Handle keyboard navigation
  useEffect(() => {
    const handleKeyDown = (event) => {
      if (!isVisible) return;

      switch (event.key) {
        case 'Escape':
          skipTour();
          break;
        case 'ArrowRight':
        case ' ':
          event.preventDefault();
          nextStep();
          break;
        case 'ArrowLeft':
          event.preventDefault();
          prevStep();
          break;
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [isVisible, currentStep]);

  // Handle resize
  useEffect(() => {
    const handleResize = () => {
      if (isVisible && highlightElement) {
        setTimeout(() => positionTooltip(highlightElement, currentStep), 100);
      }
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, [isVisible, highlightElement, currentStep]);

  if (!isVisible || tourSteps.length === 0) {
    return null;
  }

  const currentTourStep = tourSteps[currentStep];
  const isFirstStep = currentStep === 0;
  const isLastStep = currentStep === tourSteps.length - 1;

  return (
    <>
      {/* Overlay with spotlight effect */}
      <div
        ref={overlayRef}
        className="fixed inset-0 bg-black bg-opacity-75 z-40 transition-all duration-300"
        style={{
          clipPath: highlightElement ? 
            `polygon(0% 0%, 0% 100%, ${highlightElement.getBoundingClientRect().left - 8}px 100%, ${highlightElement.getBoundingClientRect().left - 8}px ${highlightElement.getBoundingClientRect().top - 8}px, ${highlightElement.getBoundingClientRect().right + 8}px ${highlightElement.getBoundingClientRect().top - 8}px, ${highlightElement.getBoundingClientRect().right + 8}px ${highlightElement.getBoundingClientRect().bottom + 8}px, ${highlightElement.getBoundingClientRect().left - 8}px ${highlightElement.getBoundingClientRect().bottom + 8}px, ${highlightElement.getBoundingClientRect().left - 8}px 100%, 100% 100%, 100% 0%)` :
            'none'
        }}
        onClick={skipTour}
      />

      {/* Tour tooltip */}
      <div
        ref={tooltipRef}
        className="fixed z-50 bg-white rounded-lg shadow-2xl border border-gray-200 max-w-sm w-full"
        style={{ position: 'fixed' }}
      >
        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b border-gray-200">
          <div className="flex items-center space-x-2">
            <Lightbulb className="w-5 h-5 text-blue-500" />
            <span className="font-semibold text-gray-800">
              {currentTourStep.title}
            </span>
          </div>
          <button
            onClick={skipTour}
            className="text-gray-400 hover:text-gray-600 transition-colors"
            aria-label="Close tour"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Content */}
        <div className="p-4">
          <p className="text-gray-600 text-sm leading-relaxed mb-4">
            {currentTourStep.content}
          </p>

          {/* Progress indicator */}
          <div className="flex items-center justify-center space-x-1 mb-4">
            {tourSteps.map((_, index) => (
              <div
                key={index}
                className={`w-2 h-2 rounded-full transition-colors ${
                  index === currentStep ? 'bg-blue-500' : 
                  index < currentStep ? 'bg-blue-300' : 'bg-gray-200'
                }`}
              />
            ))}
          </div>

          {/* Navigation */}
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-2">
              <span className="text-xs text-gray-500">
                Step {currentStep + 1} of {tourSteps.length}
              </span>
              {!isFirstStep && (
                <button
                  onClick={prevStep}
                  className="flex items-center space-x-1 text-xs text-blue-600 hover:text-blue-800 transition-colors"
                >
                  <ArrowLeft className="w-3 h-3" />
                  <span>Back</span>
                </button>
              )}
            </div>

            <div className="flex items-center space-x-2">
              <button
                onClick={skipTour}
                className="px-3 py-1 text-xs text-gray-500 hover:text-gray-700 transition-colors"
              >
                Skip Tour
              </button>
              <button
                onClick={nextStep}
                className={`flex items-center space-x-1 px-3 py-1 text-xs font-medium rounded transition-colors ${
                  isLastStep 
                    ? 'bg-green-500 hover:bg-green-600 text-white' 
                    : 'bg-blue-500 hover:bg-blue-600 text-white'
                }`}
              >
                <span>{isLastStep ? 'Complete' : 'Next'}</span>
                {isLastStep ? (
                  <ChevronRight className="w-3 h-3" />
                ) : (
                  <ArrowRight className="w-3 h-3" />
                )}
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Keyboard shortcut hint */}
      <div className="fixed bottom-4 right-4 z-50 bg-gray-900 text-white px-3 py-2 rounded-lg text-xs shadow-lg">
        Use arrow keys or ESC to navigate
      </div>
    </>
  );
};

export default GuidedTour;